import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';

import { getPackageSubscription, updatePackageSubscription } from '../services/packageSubscriptions';
import { SUBSCRIPTIONS_COLLECTION } from '../services/packageSubscriptionMapper';
import { SourceBadge } from './Styles/PackageSubscriptions';
import {
    BackButton,
    CheckboxField,
    ErrorMessage,
    FormActions,
    FormButton,
    FormCard,
    FormField,
    FormGrid,
    FormHeader,
    FormInput,
    FormPage,
    FormSection,
    FormSectionDescription,
    FormSectionTitle,
    FormSelect,
    FormSubtitle,
    FormTextarea,
    FormTitle,
    HeaderCopy,
    LockedNotice,
} from './Styles/PackageSubscriptionForm';

const packageOptions = [
    'Pretplatnički paket: BASIC',
    'Pretplatnički paket: BETTER VALUE',
    'Pretplatnički paket: PREMIUM',
    'Pretplatnički paket: PREMIUM PUBLIC',
    'Pretplatnički paket: REVICON CLUB',
];
const yearOptions = ['2024', '2025', '2026', '2027'];

const emptyInputs = {
    user: '',
    company: '',
    customer_type: 'unknown',
    description: '',
    email: '',
    address: '',
    city: '',
    phone: '',
    zip: '',
    invoiceId: '',
};

const toDate = value => {
    if (!value) return null;
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const EditPackageSubscriptions = ({ history }) => {
    const [inputs, setInputs] = useState(emptyInputs);
    const [selectedPackage, setSelectedPackage] = useState('default');
    const [startDate, setStartDate] = useState(null);
    const [paidDate, setPaidDate] = useState(null);
    const [selectedYear, setSelectedYear] = useState('default');
    const [isPaid, setIsPaid] = useState(false);
    const [sourceCollection, setSourceCollection] = useState(SUBSCRIPTIONS_COLLECTION);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const id = history.location.state?.id;
        if (!id) {
            setError('Pretplata nije odabrana.');
            return;
        }

        getPackageSubscription(String(id))
            .then(result => {
                if (!result) {
                    setError('Pretplata nije pronađena.');
                    return;
                }

                setInputs({ ...emptyInputs, ...result.data });
                setSourceCollection(result.sourceCollection);
                setSelectedPackage(result.data.package_name || 'default');
                setSelectedYear(result.data.year || 'default');
                setIsPaid(result.data.paid === true);
                setStartDate(toDate(result.data.date));
                setPaidDate(toDate(result.data.paid_date));
            })
            .catch(() => setError('Pretplata nije mogla biti učitana.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setField = (field, value) => {
        setInputs(current => ({ ...current, [field]: value }));
    };

    const setCustomerType = value => {
        setInputs(current => ({
            ...current,
            customer_type: value,
            company: value === 'person' ? '' : current.company,
        }));
    };

    const handleSubmit = async event => {
        event.preventDefault();
        const id = history.location.state?.id;
        if (!id || saving) return;

        setSaving(true);
        setError('');

        try {
            await updatePackageSubscription(String(id), sourceCollection, {
                user: inputs.user || '',
                company: inputs.customer_type === 'person' ? '' : inputs.company || '',
                customer_type: inputs.customer_type || 'unknown',
                description: inputs.description || '',
                email: inputs.email || '',
                address: inputs.address || '',
                city: inputs.city || '',
                phone: inputs.phone || '',
                zip: inputs.zip || '',
                invoiceId: inputs.invoiceId || '',
                date: startDate ? startDate.toISOString() : '',
                paid_date: isPaid && paidDate ? paidDate.toISOString() : '',
                paid: isPaid,
                year: selectedYear,
                package_name: selectedPackage,
            });
            history.push('/pretplate');
        } catch (saveError) {
            console.error(saveError);
            setError('Promjene nisu sačuvane. Provjerite podatke i pokušajte ponovo.');
            setSaving(false);
        }
    };

    const isNormalizedSubscription = sourceCollection === SUBSCRIPTIONS_COLLECTION;

    return (
        <FormPage>
            <FormHeader>
                <HeaderCopy>
                    <BackButton
                        type="button"
                        onClick={() => history.push('/pretplate')}
                        title="Nazad na pretplate"
                        aria-label="Nazad na pretplate"
                    >
                        <i className="fas fa-arrow-left" />
                    </BackButton>
                    <div>
                        <FormTitle>Uredi pretplatu</FormTitle>
                        <FormSubtitle>Promijenite podatke o kupcu, naplati i internim bilješkama.</FormSubtitle>
                    </div>
                </HeaderCopy>
                <SourceBadge $fallback={!isNormalizedSubscription}>
                    {isNormalizedSubscription ? 'subscriptions' : 'package_subscriptions · fallback'}
                </SourceBadge>
            </FormHeader>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FormCard onSubmit={handleSubmit}>
                <FormSection>
                    <FormSectionTitle>Kupac</FormSectionTitle>
                    <FormSectionDescription>Osnovni podaci koji se prikazuju u pregledu pretplata.</FormSectionDescription>
                    <FormGrid>
                        <FormField>
                            Ime i prezime
                            <FormInput
                                value={inputs.user || ''}
                                onChange={event => setField('user', event.target.value)}
                                placeholder="Ime i prezime"
                            />
                        </FormField>
                        <FormField>
                            Tip kupca
                            <FormSelect
                                value={inputs.customer_type || 'unknown'}
                                onChange={event => setCustomerType(event.target.value)}
                            >
                                <option value="unknown">Nepoznato</option>
                                <option value="company">Kompanija</option>
                                <option value="person">Fizičko lice</option>
                            </FormSelect>
                        </FormField>
                        {inputs.customer_type !== 'person' && (
                            <FormField $full>
                                Kompanija
                                <FormInput
                                    value={inputs.company || ''}
                                    onChange={event => setField('company', event.target.value)}
                                    placeholder="Naziv kompanije"
                                />
                            </FormField>
                        )}
                    </FormGrid>
                </FormSection>

                <FormSection>
                    <FormSectionTitle>Kontakt</FormSectionTitle>
                    <FormSectionDescription>Kontakt i adresa kupca za administraciju pretplate.</FormSectionDescription>
                    <FormGrid>
                        <FormField>
                            Email
                            <FormInput
                                type="email"
                                value={inputs.email || ''}
                                onChange={event => setField('email', event.target.value)}
                                placeholder="email@primjer.ba"
                            />
                        </FormField>
                        <FormField>
                            Telefon
                            <FormInput
                                type="tel"
                                value={inputs.phone || ''}
                                onChange={event => setField('phone', event.target.value)}
                                placeholder="Broj telefona"
                            />
                        </FormField>
                        <FormField $full>
                            Adresa
                            <FormInput
                                value={inputs.address || ''}
                                onChange={event => setField('address', event.target.value)}
                                placeholder="Ulica i broj"
                            />
                        </FormField>
                        <FormField>
                            Grad
                            <FormInput
                                value={inputs.city || ''}
                                onChange={event => setField('city', event.target.value)}
                                placeholder="Grad"
                            />
                        </FormField>
                        <FormField>
                            Poštanski broj
                            <FormInput
                                value={inputs.zip || ''}
                                onChange={event => setField('zip', event.target.value)}
                                placeholder="Poštanski broj"
                            />
                        </FormField>
                    </FormGrid>
                </FormSection>

                <FormSection>
                    <FormSectionTitle>Pretplata</FormSectionTitle>
                    <FormSectionDescription>Paket, period pretplate i interna bilješka.</FormSectionDescription>
                    {isNormalizedSubscription && (
                        <LockedNotice>
                            Paket, godina i datum prijave su zaključani jer pripadaju kanonskom zapisu kupovine.
                        </LockedNotice>
                    )}
                    <FormGrid>
                        <FormField>
                            Paket
                            <FormSelect
                                disabled={isNormalizedSubscription}
                                value={selectedPackage || 'default'}
                                onChange={event => setSelectedPackage(event.target.value)}
                            >
                                <option value="default">Izaberite paket</option>
                                {packageOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </FormSelect>
                        </FormField>
                        <FormField>
                            Godina
                            <FormSelect
                                disabled={isNormalizedSubscription}
                                value={selectedYear || 'default'}
                                onChange={event => setSelectedYear(event.target.value)}
                            >
                                <option value="default">Odaberite godinu</option>
                                {yearOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </FormSelect>
                        </FormField>
                        <FormField>
                            Datum prijave
                            <DatePicker
                                disabled={isNormalizedSubscription}
                                selected={startDate}
                                onChange={setStartDate}
                                dateFormat="dd.MM.yyyy"
                                placeholderText="Odaberite datum"
                            />
                        </FormField>
                        <FormField $full>
                            Opis
                            <FormTextarea
                                value={inputs.description || ''}
                                onChange={event => setField('description', event.target.value)}
                                placeholder="Interna bilješka o pretplati"
                            />
                        </FormField>
                    </FormGrid>
                </FormSection>

                <FormSection>
                    <FormSectionTitle>Naplata</FormSectionTitle>
                    <FormSectionDescription>Status plaćanja, predračun i datum uplate.</FormSectionDescription>
                    <FormGrid>
                        <CheckboxField>
                            <input
                                type="checkbox"
                                checked={isPaid}
                                onChange={event => setIsPaid(event.currentTarget.checked)}
                            />
                            Pretplata je plaćena
                        </CheckboxField>
                        <FormField>
                            Predračun
                            <FormInput
                                value={inputs.invoiceId || ''}
                                onChange={event => setField('invoiceId', event.target.value)}
                                placeholder="Broj predračuna"
                            />
                        </FormField>
                        {isPaid && (
                            <FormField>
                                Datum plaćanja
                                <DatePicker
                                    selected={paidDate}
                                    onChange={setPaidDate}
                                    dateFormat="dd.MM.yyyy"
                                    placeholderText="Odaberite datum"
                                />
                            </FormField>
                        )}
                    </FormGrid>
                </FormSection>

                <FormActions>
                    <FormButton type="button" onClick={() => history.push('/pretplate')}>
                        Odustani
                    </FormButton>
                    <FormButton type="submit" $primary disabled={saving}>
                        <i className="fas fa-save" /> {saving ? 'Čuvanje…' : 'Sačuvaj promjene'}
                    </FormButton>
                </FormActions>
            </FormCard>
        </FormPage>
    );
};

export default withRouter(EditPackageSubscriptions);
